import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { isNumber } from "lodash";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";

// Layouts
import AdvancedSelectLabel from "@Layouts/components/InputComponents/AdvancedSelectLabel";

// Components
import PercentOfTotal from "@PayPlans/components/PercentOfTotal";
import RequiredMetrics from "@PayPlans/components/RequiredMetrics";
import RequiredReferences from "@/Layouts/components/RequiredReferences";

// MUI
import { Grid, Stack, Typography } from "@mui/material";

const useAvailableOptions = (options = [], selectedOptions = []) => {
	return options
		?.filter((option) => !selectedOptions.some((item) => Boolean(item?.metricName) && item.metricName === option.metricName))
		.map((option) => ({ metricName: option.metricName }));
};

const getReference = (references = [], reference = "") => {
	if (!references?.length || !reference) {
		return null;
	}
	return references.find((element) => element.reference === reference);
};

const hasAllRequiredMetrics = (metrics = []) => metrics.every((metric) => metric.required);

const updateRequiredTiers = (metrics = [], tiers = []) => {
	const hasInitializedMetrics = Boolean(metrics?.length);
	const mandatoryRequired = metrics.filter((metric) => metric.required).length || 0;

	// Required number limits
	const maxNumberRequired = metrics?.length || 0;
	const minNumberRequired = Math.min(maxNumberRequired, mandatoryRequired) || 0;

	// Remove references from the end
	if (tiers.length > maxNumberRequired) {
		tiers.length = maxNumberRequired || 1;
	}

	return tiers.map((tier) => {
		let numberRequired = tier["numberRequired"] || 1;

		if (minNumberRequired && hasInitializedMetrics) {
			if (minNumberRequired === maxNumberRequired) {
				numberRequired = minNumberRequired;
			} else {
				const leastRequired = minNumberRequired + 1;
				const maxCurrent = Math.min(numberRequired, maxNumberRequired);
				numberRequired = Math.max(maxCurrent, leastRequired);
			}
		} else if (numberRequired > maxNumberRequired) {
			numberRequired = maxNumberRequired;
		}

		return { ...tier, numberRequired };
	});
};

const isTotalPay = (tiers = []) => {
	return tiers?.every((tier) => {
		const { missed, attained } = tier;
		return missed?.input === "Dollar" && attained?.input === "Dollar";
	});
};

const isAllReferencesValid = (
	references = [],
	metrics = [],
	definition = { missed: "missed", attained: "attained", numberRequired: "numberRequired" }
) => {
	if (!references?.length || !metrics?.length) {
		return true;
	}
	const { missed, attained, numberRequired } = definition || {};
	return references.every((reference) => (isNumber(reference[missed]?.value) || isNumber(reference[attained]?.value)) && reference[numberRequired]);
};

export default function MetricsTiers(props) {
	const {
		index = "individualMakeMetrics",
		title = "Individual Metrics",
		makeMetrics = null,
		globalInput = "",
		references = [],
		options = [],
		totalOptions = 0,
		loadingOptions = false,
		loadingReferences = false,
		emptyError = false,
		hidePercentOfTotal = false,
		readOnly = false,
		onActionInputUpdate = () => {},
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Control to send update actions
	const wasUpdated = useRef(false);
	const isValid = useRef(true);
	const isValidReferences = useRef(true);

	const onMakeMetricsUpdate = (updatedMetrics) => {
		const validMetrics = isValid.current && isValidReferences.current;
		const isAllValid = validMetrics && isAllReferencesValid(updatedMetrics?.tiers || [], updatedMetrics?.metrics || []);
		onActionValidation?.(isAllValid);
		if (isAllValid && wasUpdated.current) {
			wasUpdated.current = false;
			onActionUpdate(index, updatedMetrics);
		}
	};

	// States
	const [innerMakeMetrics, setMakeMetrics] = useCallbackState(null, onMakeMetricsUpdate);
	const availableOptions = useAvailableOptions(options, innerMakeMetrics?.metrics || []);
	const isTotalPayDefined = isTotalPay(innerMakeMetrics?.tiers);

	const handleChangeTiers = (tiers, action) => {
		const updated = { ...innerMakeMetrics };
		const updatedTiers = updateRequiredTiers(updated["metrics"], tiers);
		updated["tiers"] = [...updatedTiers];

		if (isTotalPay(updatedTiers)) {
			updated["percentOfTotal"] = false;
		}

		if (action === "input") {
			const input = updatedTiers?.[0]?.missed?.input || "Percent";
			onActionInputUpdate(input);
		}

		if (action === "adding") {
			setMakeMetrics(updated);
		} else {
			saveMakeMetrics(updated);
		}
	};

	const handleUpdateMetrics = (metrics = []) => {
		const updated = { ...innerMakeMetrics };
		const updatedTiers = updateRequiredTiers(metrics, updated["tiers"]);

		const allRequiredMetrics = hasAllRequiredMetrics(metrics);
		if (allRequiredMetrics) {
			metrics = metrics.map((metric) => {
				return { ...metric, required: false };
			});
		}

		updated["tiers"] = updatedTiers;
		updated["metrics"] = metrics;
		saveMakeMetrics(updated);
	};

	const handleUpdateReference = (selected) => {
		const updated = { ...innerMakeMetrics };
		if (updated?.["reference"] !== selected?.reference) {
			updated["reference"] = selected?.reference;
			saveMakeMetrics(updated);
		}
	};

	const handleUpdateTotalPercent = (value) => {
		const updated = { ...innerMakeMetrics };
		updated["percentOfTotal"] = value;
		saveMakeMetrics(updated);
	};

	const saveMakeMetrics = (updated) => {
		wasUpdated.current = true;
		setMakeMetrics(updated);
	};

	useEffect(() => {
		const localReference = makeMetrics?.reference || references?.[0]?.reference || "";
		setMakeMetrics({ ...makeMetrics, reference: localReference });
		return () => {
			setMakeMetrics(null);
			wasUpdated.current = false;
			isValid.current = true;
			isValidReferences.current = true;
		};
	}, [globalInput, makeMetrics, references, setMakeMetrics]);

	return (
		<Grid container direction="column">
			<Grid item>
				<Typography
					className={"!font-montserrat !text-[16px] !font-medium !text-default"}
					gutterBottom
					variant="subtitle1"
					component="div"
					mb={1}
				>
					{title}
				</Typography>
			</Grid>

			<AdvancedSelectLabel
				label={"Reference"}
				data={references || []}
				selected={getReference(references, innerMakeMetrics?.reference)}
				showBy={"referenceName"}
				selectBy={"reference"}
				placeholder={"Select Reference"}
				loading={loadingReferences}
				readOnly={readOnly}
				onChangeSelection={(value) => handleUpdateReference(value)}
			/>

			<Grid item mt={2.5}>
				<RequiredMetrics
					options={availableOptions || []}
					totalOptions={totalOptions}
					metrics={innerMakeMetrics?.metrics || []}
					requiredTitle={"Required"}
					loading={loadingOptions}
					emptyError={emptyError}
					readOnly={readOnly || loadingOptions}
					onActionUpdate={(metrics) => handleUpdateMetrics(metrics)}
					onActionValidation={(isValidMetrics) => {
						isValid.current = isValidMetrics;
					}}
				/>
			</Grid>

			{!hidePercentOfTotal && (
				<Stack position="relative" justifyContent={"center"} alignItems={"flex-end"}>
					<Stack position={"absolute"} sx={{ top: "57px", right: "-25px" }}>
						<PercentOfTotal
							checked={innerMakeMetrics?.percentOfTotal}
							isTotalPay={isTotalPayDefined}
							disabled={readOnly || isTotalPayDefined}
							handleUpdate={(value) => handleUpdateTotalPercent(value)}
						/>
					</Stack>
				</Stack>
			)}

			<RequiredReferences
				metrics={innerMakeMetrics?.metrics || []}
				tiers={innerMakeMetrics?.tiers || []}
				loading={loadingOptions}
				readOnly={readOnly}
				handleChangeTiers={handleChangeTiers}
				onActionValidation={(isReferenceValid) => {
					isValidReferences.current = isReferenceValid;
				}}
			/>
		</Grid>
	);
}
MetricsTiers.propTypes = {
	index: PropTypes.string,
	title: PropTypes.string,
	globalInput: PropTypes.string,
	makeMetrics: PropTypes.object,
	references: PropTypes.array,
	options: PropTypes.array,
	totalOptions: PropTypes.number,
	loadingOptions: PropTypes.bool,
	loadingReferences: PropTypes.bool,
	emptyError: PropTypes.bool,
	hidePercentOfTotal: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionInputUpdate: PropTypes.func,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
